import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Heart, Star, Upload, ShoppingCart, MessageCircle, FileText, X, Edit, ChevronLeft, ChevronRight, Trash2, Share2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';
import BadgeAchievementModal from '../components/BadgeAchievementModal';

// Helper function to format cigar name without duplicating brand
const formatCigarDisplayName = (brand, name) => {
  if (!name) return brand || 'Unknown';
  if (!brand) return name;
  
  // Check if name starts with brand (case-insensitive)
  const brandLower = brand.toLowerCase().trim();
  const nameLower = name.toLowerCase().trim();
  
  if (nameLower.startsWith(brandLower)) {
    // Name already contains brand, just return name
    return name;
  }
  
  // Name doesn't contain brand, combine them
  return `${brand} ${name}`;
};

const CigarDetailPage = () => {
  const { id } = useParams();
  const [cigar, setCigar] = useState(null);
  const [userRating, setUserRating] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [editingFlavors, setEditingFlavors] = useState(false);
  const [flavorNotes, setFlavorNotes] = useState([]);
  const [newFlavor, setNewFlavor] = useState('');
  const [canEditFlavors, setCanEditFlavors] = useState(false);
  const [userRatingsCount, setUserRatingsCount] = useState(0);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [showRatingSuccess, setShowRatingSuccess] = useState(false);
  const [ratingFadingOut, setRatingFadingOut] = useState(false);
  const [ratingLoaded, setRatingLoaded] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editingCigar, setEditingCigar] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [earnedBadges, setEarnedBadges] = useState([]);
  const [userPhotos, setUserPhotos] = useState([]);
  const [myPhotoCount, setMyPhotoCount] = useState(0);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [addedByUser, setAddedByUser] = useState(null);
  const [hasSmoked, setHasSmoked] = useState(false);
  const [smokingLoading, setSmokingLoading] = useState(false);
  const { token, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Scroll to top when page loads or cigar id changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (!cigar) return;

    const previewVersion = (cigar.image_url || 'no-image').split('?')[0].split('/').pop() || 'no-image';
    const currentUrl = new URL(window.location.href);
    const needsVersion = !currentUrl.searchParams.has('v');
    const returnedFromShare = currentUrl.searchParams.has('from_share');

    if (needsVersion || returnedFromShare) {
      currentUrl.searchParams.set('v', previewVersion);
      currentUrl.searchParams.delete('from_share');
      window.history.replaceState(null, '', `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`);
    }
  }, [cigar]);

  // Build array of all images (main + user photos, avoiding duplicates)
  const mainImageUrl = cigar?.image_url || null;
  
  // Check if main image is a user photo (so we can show delete button)
  const mainImageUserPhoto = userPhotos.find(p => p.image_url === mainImageUrl);
  
  const filteredUserPhotos = userPhotos.filter(p => p.image_url !== mainImageUrl);
  const allImages = [
    ...(mainImageUrl ? [{ url: mainImageUrl, isMain: true, photo: mainImageUserPhoto }] : []),
    ...filteredUserPhotos.map(p => ({ url: p.image_url, isMain: false, photo: p }))
  ];
  const currentImage = allImages[currentImageIndex];
  const canDeleteCurrentPhoto = Boolean(
    token && (
      (currentImage?.photo && currentImage.photo.user_id === user?.id) ||
      (currentImage?.isMain && !currentImage.photo && cigar?.added_by === user?.id)
    )
  );

  const fetchCigar = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cigars/${id}`);
      setCigar(res.data);
      setFlavorNotes(res.data.flavor_notes || []);
      
      // Fetch the user who added this cigar
      if (res.data.added_by) {
        try {
          const userRes = await axios.get(`${API}/users/${res.data.added_by}/profile`);
          setAddedByUser(userRes.data.user);
        } catch (e) {
          console.error('Failed to fetch added_by user');
        }
      }
    } catch (error) {
      toast.error('Failed to load cigar');
    }
  }, [id]);

  const fetchUserPhotos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cigars/${id}/photos`);
      setUserPhotos(res.data || []);
    } catch (error) {
      console.error('Failed to fetch user photos');
    }
  }, [id]);

  const fetchMyPhotoCount = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/cigars/${id}/photos/mine`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPhotoCount((res.data || []).length);
    } catch (error) {
      console.error('Failed to fetch my photo count');
    }
  }, [id, token]);

  const fetchUserRating = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/ratings/my/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.exists && res.data.rating !== null) {
        setUserRating(res.data.rating);
      }
      setRatingLoaded(true);
    } catch (error) {
      console.error('Failed to fetch user rating:', error);
      setRatingLoaded(true);
    }
  }, [id, token]);

  const checkFavorite = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/favorites/check/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(res.data.is_favorite);
    } catch (error) {
      console.error('Failed to check favorite');
    }
  }, [id, token]);

  const checkSmoked = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/smoked/check/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasSmoked(res.data.is_smoked);
    } catch (error) {
      console.error('Failed to check smoked status');
    }
  }, [id, token]);

  const toggleSmoked = async () => {
    if (!token) {
      toast.error('Please sign in to track cigars');
      navigate('/auth');
      return;
    }
    
    setSmokingLoading(true);
    try {
      if (hasSmoked) {
        await axios.delete(`${API}/smoked/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasSmoked(false);
        toast.success('Removed from smoked list');
      } else {
        await axios.post(`${API}/smoked/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHasSmoked(true);
        toast.success('Marked as smoked!');
      }
    } catch (error) {
      toast.error('Failed to update smoked status');
    } finally {
      setSmokingLoading(false);
    }
  };

  const checkCanEditFlavors = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/users/me/ratings-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanEditFlavors(res.data.can_edit_flavors);
      setUserRatingsCount(res.data.count);
    } catch (error) {
      setCanEditFlavors(false);
    }
  }, [token]);

  const checkAdminStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsAdmin(res.data.is_admin);
    } catch (error) {
      setIsAdmin(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCigar();
    fetchUserPhotos();
  }, [fetchCigar, fetchUserPhotos]);

  // Wait for auth to finish loading before fetching user-specific data
  useEffect(() => {
    if (!authLoading && token) {
      fetchUserRating();
      checkFavorite();
      checkSmoked();
      checkCanEditFlavors();
      checkAdminStatus();
      fetchMyPhotoCount();
    } else if (!authLoading && !token) {
      // Not logged in, mark rating as loaded
      setRatingLoaded(true);
    }
  }, [authLoading, token, fetchUserRating, checkFavorite, checkSmoked, checkCanEditFlavors, checkAdminStatus, fetchMyPhotoCount]);

  const handleRating = async (value) => {
    if (!token) {
      navigate('/auth?reason=rate');
      return;
    }

    setRatingSubmitting(true);
    try {
      const res = await axios.post(
        `${API}/ratings`,
        { cigar_id: id, rating: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserRating(value);
      setShowRatingSuccess(true);
      setRatingFadingOut(false);
      fetchCigar();
      
      // Refresh the ratings count for "Rate X more to edit" counter
      checkCanEditFlavors();
      
      // Check for new badges earned
      if (res.data.new_badges && res.data.new_badges.length > 0) {
        // Delay badge popup to show after rating success animation
        setTimeout(() => {
          setEarnedBadges(res.data.new_badges);
        }, 1500);
      }
      
      // Start fade out after 0.75 seconds, then hide after fade completes
      setTimeout(() => {
        setRatingFadingOut(true);
        setTimeout(() => {
          setShowRatingSuccess(false);
          setRatingFadingOut(false);
        }, 500); // 500ms fade duration
      }, 750);
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const toggleFavorite = async () => {
    if (!token) {
      toast.error('Please sign in to favorite');
      navigate('/auth');
      return;
    }

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Removed from favorites');
      } else {
        await axios.post(`${API}/favorites/${id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Added to favorites');
      }
      setIsFavorite(!isFavorite);
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleAddFlavor = () => {
    if (!newFlavor.trim()) return;
    if (flavorNotes.length >= 10) {
      toast.error('Maximum 10 flavor notes allowed');
      return;
    }
    if (flavorNotes.some(f => f.toLowerCase() === newFlavor.trim().toLowerCase())) {
      toast.error('This flavor note already exists');
      return;
    }
    setFlavorNotes([...flavorNotes, newFlavor.trim()]);
    setNewFlavor('');
  };

  const handleRemoveFlavor = (index) => {
    setFlavorNotes(flavorNotes.filter((_, i) => i !== index));
  };

  const handleSaveFlavors = async () => {
    if (!token) {
      toast.error('Please sign in to edit flavor notes');
      navigate('/auth');
      return;
    }

    try {
      const res = await axios.put(
        `${API}/cigars/${id}/flavor-notes`,
        flavorNotes,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCigar(res.data);
      setEditingFlavors(false);
      toast.success('Flavor notes updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update flavor notes');
    }
  };

  const handleCancelEditFlavors = () => {
    setFlavorNotes(cigar.flavor_notes || []);
    setEditingFlavors(false);
    setNewFlavor('');
  };

  // Admin edit functions
  const handleStartEdit = () => {
    setEditForm({
      brand: cigar.brand || '',
      name: cigar.name || '',
      strength: cigar.strength || '',
      origin: cigar.origin || '',
      size: cigar.size || '',
      wrapper: cigar.wrapper || '',
      price_range: cigar.price_range || '',
    });
    setEditingCigar(true);
  };

  const handleCancelEdit = () => {
    setEditingCigar(false);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!token || !isAdmin) return;
    
    setSavingEdit(true);
    try {
      const res = await axios.put(
        `${API}/admin/cigars/${id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCigar(prev => ({ ...prev, ...res.data }));
      setEditingCigar(false);
      toast.success('Cigar updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update cigar');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteCigar = async () => {
    if (!token || !isAdmin) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete "${formatCigarDisplayName(cigar?.brand, cigar?.name)}"?\n\nThis will also delete all ratings, comments, favorites, notes, and photos associated with this cigar.\n\nThis action cannot be undone!`
    );
    
    if (!confirmed) return;
    
    try {
      await axios.delete(
        `${API}/admin/cigars/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Cigar deleted successfully!');
      // Go back to the previous page, or home if no history
      if (window.history.length > 2) {
        navigate(-1);
      } else {
        navigate('/');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete cigar');
    }
  };

  const handleUploadUserPhoto = async (file) => {
    if (!token) {
      toast.error('Please sign in to upload photos');
      navigate('/auth');
      return;
    }

    if (myPhotoCount >= 5) {
      toast.error('You can only upload 5 photos per cigar');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(`${API}/cigars/${id}/photos`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Photo uploaded!');
      fetchUserPhotos();
      fetchMyPhotoCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeleteCurrentPhoto = async () => {
    if (!canDeleteCurrentPhoto) return;
    const photo = currentImage.photo;
    const isMainImage = currentImage.isMain;
    const message = isMainImage
      ? 'This photo is the main image. Deleting it will remove it from the cigar. Continue?'
      : 'Delete this photo?';
    if (!window.confirm(message)) return;

    try {
      const endpoint = photo
        ? `${API}/cigars/${id}/photos/${photo.id}`
        : `${API}/cigars/${id}/main-photo`;
      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Photo deleted');
      await fetchCigar();
      await fetchUserPhotos();
      fetchMyPhotoCount();
      setCurrentImageIndex(0);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete photo');
    }
  };

  const copyShareUrl = async (shareUrl) => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      const temporaryInput = document.createElement('textarea');
      temporaryInput.value = shareUrl;
      temporaryInput.setAttribute('readonly', '');
      temporaryInput.style.position = 'fixed';
      temporaryInput.style.opacity = '0';
      document.body.appendChild(temporaryInput);
      temporaryInput.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(temporaryInput);
      if (copied) {
        toast.success('Link copied to clipboard!');
      } else {
        toast.error('Unable to copy the link. Please copy it from your address bar.');
      }
    }
  };

  if (!cigar) return <div className="min-h-screen bg-[#0B0B0C] flex items-center justify-center text-white">Loading...</div>;

  // Build meta description
  const metaDescription = [
    cigar.strength && `Strength: ${cigar.strength}`,
    cigar.origin && `Origin: ${cigar.origin}`,
    cigar.average_rating && `Rating: ${cigar.average_rating.toFixed(1)}/10`
  ].filter(Boolean).join(' | ') || 'Discover this cigar on Cigar Ranker';

  const pageTitle = formatCigarDisplayName(cigar.brand, cigar.name);
  const pageUrl = `${window.location.origin}/cigar/${id}`;
  const imageUrl = cigar.image_url || `${window.location.origin}/og-image.png`;
  const shareVersion = (cigar.image_url || 'no-image').split('?')[0].split('/').pop() || 'no-image';

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-24">
      {/* Dynamic Meta Tags for Social Sharing */}
      <Helmet>
        <title>{pageTitle} - Cigar Ranker</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Cigar Ranker" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <div className="max-w-md mx-auto">
        {/* Back Button & Share & Favorite */}
        <div className="flex items-center justify-between p-4">
          <button data-testid="back-button" onClick={() => navigate(-1)} className="text-white">
            ← Back
          </button>
          <div className="flex items-center gap-3">
            <button 
              data-testid="share-button" 
              onClick={() => {
                const shareUrl = `${window.location.origin}/api/cigars/og/${id}?v=${encodeURIComponent(shareVersion)}`;
                const shareText = `Check out ${pageTitle} on Cigar Ranker!`;
                
                if (navigator.share) {
                  navigator.share({
                    title: pageTitle,
                    text: shareText,
                    url: shareUrl
                  }).catch(() => {});
                } else {
                  copyShareUrl(shareUrl);
                }
              }}
              className="text-white/50 hover:text-white"
            >
              <Share2 size={22} />
            </button>
            <button data-testid="favorite-toggle" onClick={toggleFavorite}>
              <Heart className={isFavorite ? 'text-red-500 fill-red-500' : 'text-white/50'} />
            </button>
          </div>
        </div>

        {/* Cigar Image Carousel */}
        <div className="relative bg-[#141416] h-80">
          {allImages.length > 0 ? (
            <div className="relative w-full h-full overflow-hidden">
              {/* Blurred background image */}
              <img 
                src={allImages[currentImageIndex]?.url} 
                alt=""
                className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-50"
                onError={(e) => { e.target.src = CIGAR_PLACEHOLDER; }}
              />
              {/* Main image */}
              <img 
                src={allImages[currentImageIndex]?.url} 
                alt={cigar.name} 
                className="relative w-full h-full object-contain z-10" 
                onError={(e) => { e.target.src = CIGAR_PLACEHOLDER; }}
              />
              
              {/* Navigation arrows - only show if multiple images */}
              {allImages.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
                  >
                    <ChevronLeft size={24} />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white"
                  >
                    <ChevronRight size={24} />
                  </button>
                </>
              )}
              
              {/* Image indicator dots */}
              {allImages.length > 1 && (
                <div className="absolute top-3 left-0 right-0 flex justify-center gap-1.5 z-20">
                  {allImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentImageIndex ? 'bg-amber-500' : 'bg-white/40'
                      }`}
                    />
                  ))}
                </div>
              )}
              
              {/* Photo credit for user photos */}
              {!allImages[currentImageIndex]?.isMain && allImages[currentImageIndex]?.photo && (
                <div className="absolute top-3 right-3 z-20 bg-black/60 px-2 py-1 rounded text-xs text-white/80">
                  📷 {allImages[currentImageIndex].photo.username}
                </div>
              )}
              
              {/* Delete button for user photos (including if it became the main image) */}
              {canDeleteCurrentPhoto && (
                <button
                  data-testid="delete-current-photo-button"
                  aria-label="Delete current photo"
                  onClick={handleDeleteCurrentPhoto}
                  className="absolute top-3 left-3 z-20 p-2 bg-red-600/80 hover:bg-red-600 rounded-full text-white"
                >
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ) : (
            <div className="w-full h-full bg-[#141416] flex flex-col items-center justify-center relative overflow-hidden">
              <img src={CIGAR_PLACEHOLDER} alt="Cigar" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <p className="text-white/80 mb-2 text-sm bg-black/50 px-3 py-1 rounded">No photo yet</p>
              </div>
            </div>
          )}
          
          {/* Upload button */}
          <div className="absolute bottom-4 left-0 right-0 flex justify-center z-30">
            <label className={`cursor-pointer ${uploadingPhoto || myPhotoCount >= 5 ? 'opacity-50 pointer-events-none' : ''}`}>
              <input
                data-testid="upload-image-input"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  if (e.target.files[0]) {
                    handleUploadUserPhoto(e.target.files[0]);
                  }
                }}
                className="hidden"
                disabled={uploadingPhoto || myPhotoCount >= 5}
              />
              <div className="bg-amber-600 hover:bg-amber-700 px-6 py-2 rounded-full text-white flex items-center gap-2 shadow-lg border border-amber-500">
                <Upload size={16} />
                {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                {myPhotoCount > 0 && myPhotoCount < 5 && (
                  <span className="text-xs opacity-75">({5 - myPhotoCount} left)</span>
                )}
              </div>
            </label>
          </div>
          
          {/* Image count badge */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 right-4 z-20 bg-black/60 px-2 py-1 rounded text-xs text-white">
              {currentImageIndex + 1} / {allImages.length}
            </div>
          )}
        </div>

        {/* Cigar Info */}
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <p className="text-sm text-white/50 mb-1">{cigar.brand}</p>
              <h1 className="playfair text-3xl text-white font-bold">{formatCigarDisplayName(cigar.brand, cigar.name)}</h1>
              <div className="flex items-center gap-2 mt-2">
                <Star className="text-amber-500 fill-amber-500" />
                <span className="text-amber-500 font-bold text-xl">{cigar.average_rating.toFixed(1)}</span>
                <span className="text-white/50">({cigar.rating_count} {cigar.rating_count === 1 ? 'rating' : 'ratings'})</span>
              </div>
            </div>
            {/* Admin Edit Button */}
            {isAdmin && !editingCigar && (
              <button
                onClick={handleStartEdit}
                className="bg-amber-600 hover:bg-amber-700 p-2 rounded-lg text-white flex items-center gap-1 text-sm"
              >
                <Edit size={16} />
                Edit
              </button>
            )}
          </div>

          {/* Admin Edit Form */}
          {editingCigar && isAdmin && (
            <div className="bg-white/5 border border-amber-500/30 rounded-xl p-4 space-y-3">
              <h3 className="text-amber-500 font-bold text-sm">Admin Edit Mode</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-white/50 text-xs">Brand</label>
                  <Input
                    value={editForm.brand || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, brand: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs">Name</label>
                  <Input
                    value={editForm.name || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs">Strength</label>
                  <Input
                    value={editForm.strength || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, strength: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                    placeholder="mild/medium/full"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs">Origin</label>
                  <Input
                    value={editForm.origin || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, origin: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs">Size</label>
                  <Input
                    value={editForm.size || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, size: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
                <div>
                  <label className="text-white/50 text-xs">Wrapper</label>
                  <Input
                    value={editForm.wrapper || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, wrapper: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                  />
                </div>
                <div className="col-span-2">
                  <label className="text-white/50 text-xs">Price Range</label>
                  <Input
                    value={editForm.price_range || ''}
                    onChange={(e) => setEditForm(prev => ({ ...prev, price_range: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white text-sm h-8"
                    placeholder="e.g., $10-15 per stick"
                  />
                </div>
              </div>
              
              {/* Image Management Section */}
              <div className="border-t border-white/10 pt-3 mt-3">
                <label className="text-white/50 text-xs block mb-2">Main Photo</label>
                <div className="flex gap-2">
                  {cigar.image_url ? (
                    <Button
                      onClick={async () => {
                        if (!window.confirm('Are you sure you want to remove the main photo? This cannot be undone.')) return;
                        try {
                          const response = await axios.put(
                            `${API}/admin/cigars/${id}`,
                            { clear_image: true },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (response.data) {
                            toast.success('Photo removed');
                            setCigar(prev => ({ ...prev, image_url: null, r2_key: null }));
                          }
                        } catch (error) {
                          toast.error('Failed to remove photo');
                        }
                      }}
                      variant="outline"
                      className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 text-sm h-8"
                    >
                      <Trash2 size={14} className="mr-1" />
                      Remove Photo
                    </Button>
                  ) : (
                    <p className="text-white/30 text-sm italic">No main photo set</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={handleCancelEdit}
                  variant="outline"
                  className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  {savingEdit ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
              <div className="border-t border-white/10 pt-3 mt-3">
                <Button
                  onClick={handleDeleteCigar}
                  variant="destructive"
                  className="w-full bg-red-600 hover:bg-red-700 text-white flex items-center justify-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete Cigar
                </Button>
                <p className="text-red-400/70 text-xs mt-2 text-center">
                  This will permanently delete the cigar and all associated data
                </p>
              </div>
            </div>
          )}

          {!editingCigar && (
            <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-white/50">Strength:</p>
              <p className="text-white">{cigar.strength || 'medium'}</p>
            </div>
            <div>
              <p className="text-white/50">Origin:</p>
              <p className="text-white">{cigar.origin || 'Nicaragua'}</p>
            </div>
            <div>
              <p className="text-white/50">Size:</p>
              <p className="text-white">{cigar.size || 'Gordo, Perfecto, Robusto, Toro'}</p>
            </div>
            <div>
              <p className="text-white/50">Wrapper:</p>
              <p className="text-white">{cigar.wrapper || 'Ecuador Connecticut'}</p>
            </div>
            <div>
              <p className="text-white/50">Price Range:</p>
              <div className="flex items-center gap-2">
                <p className="text-white">{cigar.price_range || 'Unknown'}</p>
                {token && (
                  <button
                    onClick={async () => {
                      setFetchingPrice(true);
                      try {
                        const res = await axios.post(
                          `${API}/cigars/${id}/fetch-price`,
                          {},
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        if (res.data.status === 'OK') {
                          toast.success(`Price updated: ${res.data.price_range}`);
                          fetchCigar(); // Refresh cigar data
                        } else {
                          toast.error(res.data.message || 'Could not fetch price');
                        }
                      } catch (error) {
                        toast.error('Failed to fetch price');
                      } finally {
                        setFetchingPrice(false);
                      }
                    }}
                    disabled={fetchingPrice}
                    className="text-amber-500 text-xs hover:text-amber-400 underline disabled:opacity-50"
                  >
                    {fetchingPrice ? 'Fetching...' : 'Update Price'}
                  </button>
                )}
              </div>
            </div>
            
            {/* Smoked Checkbox */}
            <div className="col-span-2 pt-3 border-t border-white/10 mt-3">
              <label 
                className="flex items-center gap-3 cursor-pointer group"
                onClick={toggleSmoked}
              >
                <div className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                  hasSmoked 
                    ? 'bg-amber-600 border-amber-600' 
                    : 'border-white/30 group-hover:border-amber-500'
                } ${smokingLoading ? 'opacity-50' : ''}`}>
                  {hasSmoked && (
                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
                <span className={`text-sm ${hasSmoked ? 'text-amber-500' : 'text-white/70 group-hover:text-white'}`}>
                  {smokingLoading ? 'Updating...' : hasSmoked ? "I've smoked this cigar" : "Mark as smoked"}
                </span>
              </label>
            </div>
          </div>
          )}

          {/* Flavor Notes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-white font-bold">Flavor Notes</h3>
              {token && (
                !editingFlavors ? (
                  canEditFlavors ? (
                    <button 
                      onClick={() => setEditingFlavors(true)}
                      className="text-amber-500 text-sm hover:text-amber-400"
                    >
                      Edit
                    </button>
                  ) : (
                    <span className="text-white/30 text-xs">
                      Rate {10 - userRatingsCount} more to edit
                    </span>
                  )
                ) : (
                  <div className="flex gap-2">
                    <button 
                      onClick={handleCancelEditFlavors}
                      className="text-white/50 text-sm hover:text-white"
                    >
                      Cancel
                    </button>
                    <button 
                      onClick={handleSaveFlavors}
                      className="text-amber-500 text-sm hover:text-amber-400"
                    >
                      Save
                    </button>
                  </div>
                )
              )}
            </div>
            
            {editingFlavors ? (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {flavorNotes.map((note, idx) => (
                    <span 
                      key={idx} 
                      className="bg-white/5 border border-white/10 px-3 py-1 rounded-full text-sm text-white flex items-center gap-2"
                    >
                      {note}
                      <button 
                        onClick={() => handleRemoveFlavor(idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X size={14} />
                      </button>
                    </span>
                  ))}
                </div>
                {flavorNotes.length < 10 && (
                  <div className="flex gap-2">
                    <Input
                      value={newFlavor}
                      onChange={(e) => setNewFlavor(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddFlavor()}
                      placeholder="Add flavor note..."
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/30 flex-1"
                      maxLength={30}
                    />
                    <Button 
                      onClick={handleAddFlavor}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Add
                    </Button>
                  </div>
                )}
                <p className="text-white/40 text-xs">{flavorNotes.length}/10 flavor notes</p>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {(flavorNotes.length > 0 ? flavorNotes : ['Tobacco', 'Wood', 'Spice', 'Cream']).map((note, idx) => (
                  <span key={idx} className="bg-white/5 border border-white/10 px-4 py-1 rounded-full text-sm text-white">
                    {note}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Rate This Cigar */}
          <div>
            <h3 className="text-white font-bold mb-2">Rate this cigar</h3>
            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="text-4xl text-white">{userRating.toFixed(1)}</span>
                <Star className="text-amber-500 fill-amber-500" size={32} />
              </div>
              <Slider
                data-testid="rating-slider"
                value={[userRating]}
                onValueChange={(value) => setUserRating(value[0])}
                min={0}
                max={10}
                step={0.1}
                className="mb-4"
              />
              <div className="flex justify-between text-xs text-white/50">
                <span>0</span>
                <span>5</span>
                <span>10</span>
              </div>
            </div>
            <Button
              data-testid="submit-rating-button"
              onClick={() => handleRating(userRating)}
              disabled={ratingSubmitting}
              className={`w-full mt-4 text-white rounded-full py-6 transition-all duration-200 transform ${
                ratingSubmitting 
                  ? 'bg-amber-400 scale-95' 
                  : 'bg-gradient-to-br from-amber-600 to-amber-800 hover:scale-[1.02] active:scale-95'
              }`}
            >
              {ratingSubmitting ? '✓ Submitting...' : 'Submit Rating'}
            </Button>
            
            {/* Rating Success Celebration */}
            {showRatingSuccess && (
              <div className={`fixed inset-0 pointer-events-none z-50 flex items-center justify-center transition-opacity duration-500 ${ratingFadingOut ? 'opacity-0' : 'opacity-100'}`}>
                <div className="animate-bounce">
                  <div className="bg-gradient-to-br from-amber-500 to-amber-700 text-white px-8 py-6 rounded-2xl shadow-2xl shadow-amber-500/30 flex flex-col items-center gap-2 animate-pulse">
                    <div className="text-5xl mb-2">⭐</div>
                    <div className="text-2xl font-bold">Rating Saved!</div>
                    <div className="text-amber-200 text-lg">{userRating.toFixed(1)} / 10</div>
                  </div>
                </div>
                {/* Confetti-like stars */}
                <div className="absolute inset-0 overflow-hidden">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute text-2xl animate-ping"
                      style={{
                        left: `${10 + (i * 7)}%`,
                        top: `${20 + (i % 3) * 20}%`,
                        animationDelay: `${i * 0.1}s`,
                        animationDuration: '1s'
                      }}
                    >
                      ✨
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Comments/Ratings */}
          <button
            data-testid="discussions-button"
            onClick={() => navigate(`/cigar/${id}/discussions`)}
            className="w-full bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full py-6 flex items-center justify-center gap-2 transition-colors"
          >
            <MessageCircle size={20} />
            Comments/Ratings
          </button>

          {/* Notes */}
          <button
            data-testid="notes-button"
            onClick={() => {
              if (!token) {
                toast.error('Please sign in to view notes');
                navigate('/auth');
                return;
              }
              navigate(`/cigar/${id}/notes`);
            }}
            className="w-full bg-white/5 border border-white/10 hover:bg-white/10 text-white rounded-full py-6 flex items-center justify-center gap-2 transition-colors"
          >
            <FileText size={20} />
            Notes
          </button>

          {/* Where to Buy */}
          <Button
            data-testid="where-to-buy-button"
            disabled
            className="w-full bg-white/10 text-white/40 rounded-full py-6 flex items-center justify-center gap-2 cursor-not-allowed"
          >
            <ShoppingCart size={20} />
            Where to Buy
            <span className="text-xs ml-1">(Coming Soon)</span>
          </Button>

          {/* Added By Section */}
          {addedByUser && (
            <div className="mt-6 pt-4 border-t border-white/10 mb-20">
              <p className="text-white/40 text-sm text-center mb-2">Added by</p>
              <button
                onClick={() => navigate(`/user/${addedByUser.id}`)}
                className="w-full flex items-center justify-center gap-3 p-3 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
              >
                <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
                  {addedByUser.profile_pic ? (
                    <img 
                      src={addedByUser.profile_pic} 
                      alt={addedByUser.username}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/50 text-lg font-bold">
                      {addedByUser.username?.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                <span className="text-white font-medium">{addedByUser.username}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <BottomNav />

      {/* Badge Achievement Modal */}
      {earnedBadges.length > 0 && (
        <BadgeAchievementModal
          badges={earnedBadges}
          onClose={() => setEarnedBadges([])}
        />
      )}
    </div>
  );
};

export default CigarDetailPage;
